import * as React from 'react';

import {
  enumAddressType,
  enumCustomAttributeEntityType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  Select,
  SelectCountry,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding } from 'lucide-react';

import {
  $locationForm,
  $resetLocationForm,
  $updateLocationForm,
} from '../../_state/locationForm.state';
import {
  $createFacility,
  $isLocationCreationDialogOpen,
  $rawLocationList,
} from '../../_state/locations.state';
import { FormSection } from '../_shared/FormSection';

import { CustomAttributeFieldsSection } from '~/shared/components/custom-attributes/CustomAttributesSection';
import { states as usStates } from '~/shared/enumerables/usStates';
import { useFacilityGroupsQuery } from '~/shared/graphql/queries/useFacilityGroupsQuery';

export const LocationCreationDialog = () => {
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom($isLocationCreationDialogOpen);
  const form = useAtomValue($locationForm);
  const updateForm = useSetAtom($updateLocationForm);
  const resetForm = useSetAtom($resetLocationForm);
  const [{ isPending }, createFacility] = useAtom($createFacility);
  const locationList = useAtomValue($rawLocationList);

  const { data: facilityGroups } = useFacilityGroupsQuery({
    limit: 100,
  });

  const [customAttributes, setCustomAttributes] = React.useState<
    Record<string, boolean | string | number | null | undefined>
  >({});

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
      setCustomAttributes({});
    }
  }, [isOpen, resetForm]);

  const facilityGroupOptions = React.useMemo(
    () =>
      facilityGroups?.map((group) => ({
        key: group.id,
        label: group.name,
      })) ?? [],
    [facilityGroups],
  );

  const timezoneOptions = React.useMemo(
    () =>
      Intl.supportedValuesOf('timeZone').map((tz) => ({
        key: tz,
        label: tz.replaceAll('_', ' '),
        searchable: tz,
      })),
    [],
  );

  const usStateOptions = React.useMemo(
    () =>
      usStates.map((it) => ({
        key: it.abbreviation,
        label: it.name,
        searchable: `${it.name} ${it.abbreviation}`,
      })),
    [],
  );

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: unknown) => {
      updateForm({
        [field]: value as (typeof form)[K],
      });
    };

  const handleAddressFieldChange =
    (field: keyof typeof form.address) => (value: unknown) => {
      updateForm({
        address: {
          ...form.address,
          [field]: value as string,
        },
      });
    };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
    setCustomAttributes({});
  };

  const handleCreate = async () => {
    try {
      await createFacility({
        facilityName: form.facilityName,
        facilityType: form.facilityType,
        facilityGroupId: form.facilityGroupId,
        timezone: form.timezone,
        address: {
          streetAddress: form.address.streetAddress,
          streetAddress2: form.address.addressLine2,
          city: form.address.city,
          state: form.address.state,
          country: form.address.country,
          postalCode: form.address.postalCode,
          addressType: enumAddressType.Standard,
          isMailingAddress: true,
        },
        customAttributes:
          Object.keys(customAttributes).length > 0
            ? customAttributes
            : undefined,
      });
      toast.success('Facility created successfully');
      locationList.refetch?.();
      handleClose();
    } catch (error) {
      console.error('Failed to create facility:', error);
      toast.error('Failed to create facility');
    }
  };

  const canSubmit =
    form.facilityName &&
    form.facilityType &&
    form.timezone &&
    form.address.streetAddress &&
    form.address.country &&
    form.address.postalCode &&
    form.address.city &&
    form.address.state;

  return (
    <ConsoleDialog
      className="@LocationCreationDialog"
      title="Add Facility"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={
        form.facilityName !== '' ||
        form.facilityType !== '' ||
        form.address.streetAddress !== ''
      }
      content={
        <Col gap6>
          <FormSection icon={LucideBuilding} title="Facility Info">
            <Col gap4>
              <TextField
                label="Location Name"
                value={form.facilityName}
                onChange={handleFieldChange('facilityName')}
                isRequired
              />

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label="Facility Type"
                  value={form.facilityType}
                  onChange={handleFieldChange('facilityType')}
                  isRequired
                />
                <Select
                  label="Facility Group"
                  value={form.facilityGroupId}
                  onChange={(value) =>
                    handleFieldChange('facilityGroupId')(value || undefined)
                  }
                  options={facilityGroupOptions}
                  placeholder="Select..."
                  isRequired
                />
              </div>

              <Select
                label="Timezone"
                value={form.timezone}
                onChange={handleFieldChange('timezone')}
                options={timezoneOptions}
                isRequired
              />

              <TextField
                label="Address #1"
                value={form.address.streetAddress}
                onChange={handleAddressFieldChange('streetAddress')}
                isRequired
              />

              <TextField
                label="Address #2"
                value={form.address.addressLine2 || ''}
                onChange={handleAddressFieldChange('addressLine2')}
              />

              <div className="grid grid-cols-2 gap-4">
                <SelectCountry
                  label="Country"
                  value={form.address.country}
                  onSelectionChange={(value) =>
                    handleAddressFieldChange('country')(value || '')
                  }
                  isRequired
                />
                <TextField
                  label="ZIP Code"
                  value={form.address.postalCode}
                  onChange={handleAddressFieldChange('postalCode')}
                  isRequired
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label="City"
                  value={form.address.city}
                  onChange={handleAddressFieldChange('city')}
                  isRequired
                />
                {form.address.country === 'US' ? (
                  <Select
                    label="State"
                    value={form.address.state}
                    onChange={handleAddressFieldChange('state')}
                    options={usStateOptions}
                    placeholder="Select..."
                    isRequired
                  />
                ) : (
                  <TextField
                    label="State"
                    value={form.address.state}
                    onChange={handleAddressFieldChange('state')}
                    isRequired
                  />
                )}
              </div>
            </Col>
          </FormSection>

          <FormSection icon={LucideBuilding} title="Custom Fields">
            <CustomAttributeFieldsSection
              entityType={enumCustomAttributeEntityType.CoreFacility}
              columns={2}
              values={customAttributes}
              onChange={setCustomAttributes}
            />
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleCreate}
        >
          Create Facility
        </Button>
      }
    />
  );
};
